import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import { useDispatch } from "react-redux";
import { handleLogin, handleLogout } from "../../../../data/redux/coreActions";
import { jwtDecode } from "jwt-decode"
import { clearAuthToken } from "../../../../utils/ajax"

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: (user: UserModel | undefined) => void
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: authHelper.getUser(),
  setCurrentUser: (user: UserModel | undefined) => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC = ({children}) => {
  const dispatch = useDispatch();
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>(authHelper.getUser())

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const updateCurrentUser = (user: UserModel | undefined) => {
    setCurrentUser(user)
    if (user) {
      authHelper.setUser(user)
    } else {
      authHelper.removeUser()
    }
  }

  const logout = () => {
    clearAuthToken()
    saveAuth(undefined)
    updateCurrentUser(undefined)
    dispatch(handleLogout());
  }

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser: updateCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC = ({children}) => {

  const dispatch = useDispatch();
  const {auth, logout, currentUser, setCurrentUser} = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  useEffect(() => {
    if (auth && auth.api_token) {
      try {
        // Only attempt to decode and check expiry if it looks like a JWT
        const isJWT = auth.api_token.includes('.')
        const decoded: any = isJWT ? jwtDecode(auth.api_token) : null
        const currentTime = Date.now() / 1000
        
        if (decoded && decoded.exp && decoded.exp < currentTime) {
          logout()
          return
        }

        // Sync with Redux if user exists (even if token isn't a JWT)
        if (currentUser) {
          dispatch(handleLogin({
            user: currentUser,
            token: auth.api_token,
            roles: decoded?.role || []
          }))
        }
        setShowSplashScreen(false)
      } catch (error) {
        console.error('Auth initialization error:', error)
        setShowSplashScreen(false)
      }
    } else {
      setShowSplashScreen(false)
    }
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
